import React, { useMemo, useState } from "react";
import "./Catering.css";

const PACKAGES = Array.from({ length: 8 }, (_, i) => ({
  dishes: i + 3,
  price: 299 + i * 50,
}));
const CATEGORIES = [
  {
    title: "FROM THE STREETS OF DELHI",
    description:
      "Little bites. Big flavours. Inspired by the colourful streets of Delhi.",
    items: [
      "Dahi Puri",
      "Samosa Chaat",
      "Vada Pav",
      "Paneer 65",
      "Avocado Chaat",
    ],
  },
  {
    title: "FROM THE TANDOOR",
    description:
      "Marinated with care, grilled to perfection and full of smoky flavour.",
    items: [
      "Chicken Malai Tikka",
      "Chicken Tikka",
      "Ghost Lamb Tikka",
      "Lamb Seekh Kebab",
      "Chicken Seekh Kebab",
      "Smoky Grilled Prawns",
    ],
  },
  {
    title: "CHICKEN SPECIALITIES",
    description:
      "Comforting classics, aromatic spices and flavours worth celebrating.",
    items: [
      "Butter Chicken",
      "Chicken Tikka Masala",
      "Delhi-Style Chicken Curry",
      "Mango Chicken",
    ],
  },
  {
    title: "LAMB SPECIALITIES",
    description: "Rich, aromatic and slow-cooked Indian favourites.",
    items: ["Lamb Rogan Josh", "Lamb Tikka Masala"],
  },
  {
    title: "VEGETARIAN FAVOURITES",
    description: "Full of flavour, colour and the goodness of Indian cooking.",
    items: [
      "Paneer Butter Masala",
      "Palak Paneer",
      "Dal Makhani",
      "Punjabi Chana",
      "Gobi Aaloo",
      "Dal Tadka",
    ],
  },
  {
    title: "RICE & BREADS",
    description: "The perfect accompaniments to complete your Indian feast.",
    items: [
      "Jeera Rice",
      "Plain Naan",
      "Garlic Naan",
      "Peshawari Naan",
      "Amritsari Kulcha",
      "Tandoori Roti",
    ],
  },
];
// Put photographs in public/images/catering/ using these filenames.
// Each photo is independently replaceable with the restaurant's own photography.
const dishImagePath = (dish) =>
  `/images/catering/${dish
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}.jpg`;

function DishPhoto({ dish }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="ld-dish-photo">
      {!failed ? (
        <img
          src={dishImagePath(dish)}
          alt={dish}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="ld-dish-photo-placeholder" aria-hidden="true">
          <span className="ld-dish-photo-symbol">✦</span>
          <span>PHOTO COMING SOON</span>
        </span>
      )}
    </span>
  );
}

const EVENT_TYPES = [
  "Birthday",
  "Corporate Event",
  "Family Celebration",
  "Wedding",
  "Private Party",
  "Other",
];
const nok = (value) => `NOK ${new Intl.NumberFormat("nb-NO").format(value)}`;
const todayLocal = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 10);
};

const Empty_form = {
  name: "",
  phone: "",
  email: "",
  date: "",
  time: "",
  eventType: "",
  address: "",
  requests: "",
};

export default function Catering() {
  const [guests, setGuests] = useState(10);
  const [packageSize, setPackageSize] = useState(5);
  const [selected, setSelected] = useState([]);
  const [service, setService] = useState("pickup");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [form, setForm] = useState(Empty_form);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const price = PACKAGES.find((p) => p.dishes === packageSize).price;
  const remaining = packageSize - selected.length;
  const total = useMemo(() => price * guests, [price, guests]);
  const updateForm = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const toggleDish = (dish) =>
    setSelected((prev) =>
      prev.includes(dish)
        ? prev.filter((d) => d !== dish)
        : prev.length < packageSize
          ? [...prev, dish]
          : prev,
    );
  const choosePackage = (count) => {
    setPackageSize(count);
    setSelected((prev) => prev.slice(0, count));
    setStatus({ type: "", message: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    const formData = {
      access_key: "b607f020-d5e9-49dd-9e15-0abb204854f3",
      subject: "New Catering Request - Listen to Delhi",
      from_name: "Listen to Delhi Catering Service",

      // Send a copy to the customer (Web3Forms Pro)
      // ccemail: form.email,

      name: form.name,
      email: form.email,
      phone: form.phone,
      event_date: form.date,
      event_time: form.time,
      event_type: form.eventType,
      requests: form.requests,
      guests: guests,
      package: `${packageSize} dishes`,
      price_per_person: `NOK ${price}`,
      estimated_total: `NOK ${guests * price}`,
      selected_dishes: selected.join(", "),
      fulfillment: service,
      delivery_address:
        service === "delivery" ? form.address : "Restaurant pickup",
      special_requests: form.specialRequests,
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Submission failed");
      }
      setSubmittedOrder(formData);
      setIsSubmitted(true);
      setStatus({
        type: "success",
        message: "Your catering request has been sent successfully!",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: "Unable to send request. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewRequest = () => {
    setIsSubmitted(false);
    setSubmittedOrder(null);

    setGuests(10);
    setPackageSize(5);
    setSelected([]);
    setService("pickup");

    setForm(Empty_form);

    setStatus({ type: "", message: "" });
    setSubmitting(false);

    document.getElementById("catering-enquiry")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="ld-catering" id="catering">
      <section className="ld-hero">
        <div className="ld-hero-inner">
          <p className="ld-eyebrow">LISTEN TO DELHI · OSLO</p>
          <h1>
            CATERING FOR
            <br />
            <em>EVERY OCCASION</em>
          </h1>
          <p className="ld-hero-subtitle">
            Bring the Flavours of Delhi to Your Celebration
          </p>
          <p>Your celebration. Your menu. Our kitchen.</p>
          <p className="ld-hero-copy">
            Whether you're planning an intimate gathering or a grand
            celebration, we have something for every occasion. Select the number
            of dishes you'd like to serve and create your own Indian feast.
          </p>
          <a className="ld-button ld-primary" href="#catering-packages">
            LET’S CREATE YOUR MENU <span aria-hidden="true">↗</span>
          </a>
        </div>
       
      </section>

      <div className="ld-page">
        <section className="ld-section" id="catering-packages">
          <div className="ld-section-heading">
            <span className="ld-step">01</span>
            <div>
              <p className="ld-kicker">START HERE</p>
              <h2>
                Choose Your Guests <em>& Package</em>
              </h2>
            </div>
          </div>
          <div className="ld-guests-card">
            <div>
              <h3>Number of guests</h3>
              <p>Minimum 10 guests. Larger groups welcome.</p>
            </div>
            <div className="ld-stepper">
              <button
                type="button"
                aria-label="Remove guest"
                disabled={guests <= 10}
                onClick={() => setGuests((n) => Math.max(10, n - 1))}
              >
                −
              </button>
              <output aria-live="polite">{guests}</output>
              <button
                type="button"
                aria-label="Add guest"
                onClick={() => setGuests((n) => n + 1)}
              >
                +
              </button>
            </div>
          </div>
          <h3 className="ld-subheading">Choose Your Catering Package</h3>
          <p className="ld-muted">
            Build your own menu from our selection of street food favourites,
            tandoori specialities and Indian curries.
          </p>

          <div className="ld-package-grid">
            {PACKAGES.map((p) => (
              <button
                key={p.dishes}
                type="button"
                className={`ld-package ${
                  packageSize === p.dishes ? "is-selected" : ""
                } ${p.dishes === 5 ? "is-popular" : ""}`}
                onClick={() => choosePackage(p.dishes)}
                aria-pressed={packageSize === p.dishes}
              >
                {p.dishes === 5 && (
                  <div className="ld-popular-badge">★ MOST POPULAR</div>
                )}

                <span className="ld-package-check">
                  {packageSize === p.dishes ? "✓" : ""}
                </span>

                <strong>{p.dishes} Dishes</strong>
                <span>{nok(p.price)}</span>
                <small>per person</small>
              </button>
            ))}
          </div>

          <p className="ld-note">
            * Proposed catering prices, subject to approval. Your estimate is
            calculated automatically and does not include any delivery charges.
          </p>
        </section>

        <section className="ld-section" id="catering-menu">
          <div className="ld-section-heading">
            <span className="ld-step">02</span>
            <div>
              <p className="ld-kicker">MADE BY YOU</p>
              <h2>
                A Taste of Delhi, <em>Chosen by You</em>
              </h2>
              <p className="ld-muted">
                Explore our selection of Indian favourites and create a menu
                that suits your celebration.
              </p>
            </div>
          </div>
          <div className="ld-selection-bar">
            <div>
              <strong>
                {selected.length} / {packageSize} dishes selected
              </strong>
              <span>
                {remaining > 0
                  ? `Choose ${remaining} more ${remaining === 1 ? "dish" : "dishes"}`
                  : "Your menu is complete!"}
              </span>
            </div>
            <div
              className="ld-progress-track"
              role="progressbar"
              aria-label="Dishes selected"
              aria-valuenow={selected.length}
              aria-valuemin={0}
              aria-valuemax={packageSize}
            >
              <span
                style={{ width: `${(selected.length / packageSize) * 100}%` }}
              />
            </div>
          </div>
          <div className="ld-category-grid">
            {CATEGORIES.map((category) => (
              <article className="ld-category" key={category.title}>
                <h3>{category.title}</h3>
                <p>{category.description}</p>
                <div className="ld-dishes">
                  {category.items.map((dish) => {
                    const checked = selected.includes(dish);
                    const disabled = !checked && remaining === 0;
                    return (
                      <label
                        className={`ld-dish ${checked ? "is-checked" : ""} ${disabled ? "is-disabled" : ""}`}
                        key={dish}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleDish(dish)}
                        />
                        <DishPhoto dish={dish} />
                        <span className="ld-dish-info">
                          <span className="ld-dish-name">{dish}</span>
                          <span className="ld-dish-cta">
                            {checked
                              ? "Added to menu"
                              : disabled
                                ? "Package full"
                                : "Add to menu"}
                          </span>
                        </span>
                        <span className="ld-checkbox" aria-hidden="true">
                          {checked ? "✓" : "+"}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="ld-section" id="catering-service">
          <div className="ld-section-heading">
            <span className="ld-step">03</span>
            <div>
              <p className="ld-kicker">YOUR WAY</p>
              <h2>
                We'll Take Care <em>of the Food</em>
              </h2>
              <p className="ld-muted">
                Celebrating at home, at the office or at a special venue? We'll
                help make catering simple.
              </p>
            </div>
          </div>
          <div className="ld-service-grid">
            <button
              type="button"
              className={`ld-service-option ${service === "pickup" ? "is-selected" : ""}`}
              aria-pressed={service === "pickup"}
              onClick={() => setService("pickup")}
            >
              <span className="ld-service-symbol">⌂</span>
              <span className="ld-service-title">
                COLLECT FROM OUR RESTAURANT
              </span>
              <span>
                Your freshly prepared order will be ready for collection at the
                agreed time.
              </span>
              <strong>Listen to Delhi — Frogner, Oslo</strong>
              <span className="ld-service-radio" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`ld-service-option ${service === "delivery" ? "is-selected" : ""}`}
              aria-pressed={service === "delivery"}
              onClick={() => setService("delivery")}
            >
              <span className="ld-service-symbol">↗</span>
              <span className="ld-service-title">HAVE IT DELIVERED</span>
              <span>
                Let the flavours of Delhi come to you, in Oslo and surrounding
                areas.
              </span>
              <strong>
                Availability and charges confirmed with your order.
              </strong>
              <span className="ld-service-radio" aria-hidden="true" />
            </button>
          </div>
        </section>

        <section className="ld-section" id="catering-enquiry">
          {isSubmitted && submittedOrder ? (
            <div className="ld-thankyou">
              <div className="ld-success-icon">✓</div>

              <p className="ld-kicker">REQUEST RECEIVED</p>

              <h2>
                Thank You, <em>{submittedOrder.name}!</em>
              </h2>

              <p className="ld-thankyou-description">
                Thank you for choosing Listen to Delhi! Your catering request
                has been submitted successfully. Our team will review your
                details and contact you shortly to confirm availability, pricing
                and arrangements.
              </p>

              <div className="ld-confirmation-card">
                <h3>Your Catering Request Summary</h3>

                <div className="ld-summary-line">
                  <span>Name</span>
                  <strong>{submittedOrder.name}</strong>
                </div>

                <div className="ld-summary-line">
                  <span>Email</span>
                  <strong>{submittedOrder.email}</strong>
                </div>

                <div className="ld-summary-line">
                  <span>Event date</span>
                  <strong>{submittedOrder.event_date}</strong>
                </div>

                <div className="ld-summary-line">
                  <span>Event time</span>
                  <strong>{submittedOrder.event_time}</strong>
                </div>

                <div className="ld-summary-line">
                  <span>Event type</span>
                  <strong>{submittedOrder.event_type}</strong>
                </div>

                <div className="ld-summary-line">
                  <span>Number of guests</span>
                  <strong>{submittedOrder.guests}</strong>
                </div>

                <div className="ld-summary-line">
                  <span>Selected package</span>
                  <strong>{submittedOrder.package} Dishes</strong>
                </div>

                <div className="ld-summary-line">
                  <span>Price per person</span>
                  <strong>{submittedOrder.price_per_person}</strong>
                </div>

                <div className="ld-summary-dishes">
                  <strong>Your Selected Dishes</strong>
                  <ul>
                    {submittedOrder.selected_dishes}
                  </ul>
                </div>

                <div className="ld-summary-line">
                  <span>Pickup / Delivery</span>
                  <strong>
                    {submittedOrder.fulfillment === "delivery"
                      ? "Delivery Requested"
                      : "Restaurant Pickup"}
                  </strong>
                </div>

                {submittedOrder.fulfillment === "delivery" && (
                  <div className="ld-summary-line">
                    <span>Delivery Address</span>
                    <strong>{submittedOrder.delivery_address}</strong>
                  </div>
                )}

                {submittedOrder.specialRequests && (
                  <div className="ld-summary-dishes">
                    <strong>Special Requests</strong>
                    <p>{submittedOrder.special_requests}</p>
                  </div>
                )}

                <div className="ld-total">
                  <span>Estimated Food Total</span>
                  <strong>{submittedOrder.estimated_total}</strong>
                </div>

                <p className="ld-note">
                  This is an enquiry, not a confirmed booking. Final pricing and
                  delivery charges, if any, will be confirmed by our team.
                </p>
              </div>

              <button
                type="button"
                className="ld-button ld-primary ld-new-request"
                onClick={handleNewRequest}
              >
                SUBMIT NEW CATERING REQUEST ↗
              </button>
            </div>
          ) : (
            <div>
              <div className="ld-section-heading">
                <span className="ld-step">04</span>
                <div>
                  <p className="ld-kicker">FINAL DETAILS</p>
                  <h2>
                    Almost Time <em>to Celebrate!</em>
                  </h2>
                  <p className="ld-muted">
                    Share a few details and our team will take care of the rest.
                  </p>
                </div>
              </div>
              <div className="ld-form-layout">
                <form
                  id="ld-catering-form"
                  className="ld-form"
                  onSubmit={handleSubmit}
                >
                  <h3>Contact Information</h3>
                  <div className="ld-field-grid">
                    <label>
                      Full name <span>*</span>
                      <input
                        required
                        autoComplete="name"
                        maxLength={120}
                        value={form.name}
                        onChange={(e) => updateForm("name", e.target.value)}
                        placeholder="Your full name"
                      />
                    </label>
                    <label>
                      Phone number <span>*</span>
                      <input
                        required
                        type="tel"
                        autoComplete="tel"
                        maxLength={30}
                        value={form.phone}
                        onChange={(e) => updateForm("phone", e.target.value)}
                        placeholder="+47 000 00 000"
                      />
                    </label>
                    <label className="ld-full">
                      Email address <span>*</span>
                      <input
                        required
                        type="email"
                        autoComplete="email"
                        maxLength={200}
                        value={form.email}
                        onChange={(e) => updateForm("email", e.target.value)}
                        placeholder="you@example.com"
                      />
                    </label>
                  </div>
                  <h3>Event Information</h3>
                  <div className="ld-field-grid">
                    <label>
                      Event date <span>*</span>
                      <input
                        required
                        type="date"
                        min={todayLocal()}
                        value={form.date}
                        onChange={(e) => updateForm("date", e.target.value)}
                      />
                    </label>
                    <label>
                      Preferred catering time <span>*</span>
                      <input
                        required
                        type="time"
                        value={form.time}
                        onChange={(e) => updateForm("time", e.target.value)}
                      />
                    </label>
                    <label className="ld-full">
                      Event type <span>*</span>
                      <select
                        required
                        value={form.eventType}
                        onChange={(e) =>
                          updateForm("eventType", e.target.value)
                        }
                      >
                        <option value="">Select an occasion</option>
                        {EVENT_TYPES.map((type) => (
                          <option key={type}>{type}</option>
                        ))}
                      </select>
                    </label>
                    <label className="ld-full">
                      Pickup or delivery
                      <input
                        readOnly
                        value={
                          service === "pickup"
                            ? "Collect from restaurant"
                            : "Delivery requested"
                        }
                      />
                    </label>
                    {service === "delivery" && (
                      <label className="ld-full">
                        Delivery address <span>*</span>
                        <input
                          required
                          autoComplete="street-address"
                          maxLength={400}
                          value={form.address}
                          onChange={(e) =>
                            updateForm("address", e.target.value)
                          }
                          placeholder="Street, number, postcode, city"
                        />
                      </label>
                    )}
                    <label className="ld-full">
                      Special requests, dietary requirements or allergies
                      <textarea
                        rows={5}
                        maxLength={3000}
                        value={form.requests}
                        onChange={(e) => updateForm("requests", e.target.value)}
                        placeholder="Tell us anything we should know..."
                      />
                    </label>
                  </div>

                  <button
                    className="ld-button ld-primary ld-submit"
                    type="submit"
                    disabled={submitting || selected.length !== packageSize}
                  >
                    {submitting
                      ? "SENDING YOUR REQUEST…"
                      : "SUBMIT CATERING REQUEST"}{" "}
                    <span aria-hidden="true">↗</span>
                  </button>
                  {selected.length !== packageSize && (
                    <p className="ld-inline-hint">
                      Please select {remaining} more{" "}
                      {remaining === 1 ? "dish" : "dishes"} to submit your
                      request.
                    </p>
                  )}
                  {status.message && (
                    <div role="status" className={`ld-status ${status.type}`}>
                      {status.message}
                    </div>
                  )}
                  <p className="ld-note">
                    Our team will review your request and confirm availability,
                    final pricing and arrangements before your order is
                    accepted.
                  </p>
                </form>

                <aside className="ld-summary">
                  <p className="ld-kicker">YOUR CATERING SUMMARY</p>
                  <h3>Your Delhi Feast</h3>
                  <div className="ld-summary-line">
                    <span>Number of guests</span>
                    <strong>{guests}</strong>
                  </div>
                  <div className="ld-summary-line">
                    <span>Selected package</span>
                    <strong>{packageSize} dishes</strong>
                  </div>
                  <div className="ld-summary-line">
                    <span>Price per person</span>
                    <strong>{nok(price)}</strong>
                  </div>
                  <div className="ld-summary-dishes">
                    <strong>Selected dishes</strong>
                    {selected.length ? (
                      <ul>
                        {selected.map((dish) => (
                          <li key={dish}>{dish}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No dishes selected yet</p>
                    )}
                  </div>
                  <div className="ld-summary-line">
                    <span>
                      {service === "pickup" ? "Collection" : "Delivery"}
                    </span>
                    <strong>
                      {service === "pickup"
                        ? "Restaurant pickup"
                        : "To be confirmed"}
                    </strong>
                  </div>
                  <div className="ld-total">
                    <span>Estimated food total</span>
                    <strong>{nok(total)}</strong>
                  </div>
                  <p>
                    Final price and any delivery charges will be confirmed by
                    our team.
                  </p>
                </aside>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
